import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, Download, Upload, Tags, ChevronRight, Store, Boxes, Wallet, AlertTriangle, Package, FileSpreadsheet } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { KpiCard } from '@/components/ui/KpiCard';
import { Pagination } from '@/components/ui/Pagination';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { TD_DENSITY, TH_DENSITY } from '@/components/ui/tableDensity';
import { Badge } from '@/components/ui/Badge';
import { ItemThumb } from '@/components/ui/ItemThumb';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { ExcelImportModal } from '@/components/ui/ExcelImportModal';
import { SetSisReorderModal } from './SetSisReorderModal';
import {
  listSisStock,
  listSisStores,
  listSisStockLots,
  importSisSalesReport,
  importSisGroupReport,
  sisSalesReportTemplatePath,
  sisStockExportPath,
  type SisStockRow,
  type SisStore,
} from '@/api/sisStock';
import { StoreLabelsModal } from './StoreLabelsModal';
import { downloadExcel } from '@/lib/downloadExcel';
import { formatINR, formatDate } from '@/lib/format';

const STATUS_TONE: Record<SisStockRow['stockStatus'], 'success' | 'warning' | 'danger'> = {
  'In Stock': 'success',
  'Low Stock': 'warning',
  'Out of Stock': 'danger',
};

const UNGROUPED = 'Ungrouped';
const COLS = ['', 'Store', 'Item Code', 'Barcode', 'Item Name', 'Unit', 'Qty', 'Reorder', 'Value', 'Status', ''];

export function SisStockPage() {
  const qc = useQueryClient();
  const [partyId, setPartyId] = useState('');
  const [group, setGroup] = useState('');
  const [search, setSearch] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reorderRow, setReorderRow] = useState<SisStockRow | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  useEffect(() => setPage(1), [search, group, partyId]);

  // Pin the card's title + pagination and let only the table body scroll — the
  // KPI cards, filter bar and table header stay in view (same as the shared Table).
  const containerRef = useRef<HTMLDivElement>(null);
  const titleBarRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState('calc(100vh - 340px)');

  const toggleExpanded = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const filter = useMemo(
    () => ({ partyId: partyId ? Number(partyId) : undefined }),
    [partyId],
  );

  const storesQ = useQuery({ queryKey: ['sis-stores'], queryFn: () => listSisStores(), staleTime: 0 });
  const stockQ = useQuery({
    queryKey: ['sis-stock', filter],
    queryFn: () => listSisStock(filter),
    refetchInterval: 60_000,
  });
  const stores = storesQ.data ?? [];
  const selectedStore = stores.find((s) => String(s.id) === partyId);
  // A specific store → single upload; else a chosen group → group-wise upload.
  const uploadTarget: 'store' | 'group' | null = selectedStore ? 'store' : group ? 'group' : null;

  // Stores grouped for the dropdown's <optgroup>s.
  const storesByGroup = useMemo(() => {
    const m = new Map<string, SisStore[]>();
    for (const s of stores) {
      const g = s.group || UNGROUPED;
      if (!m.has(g)) m.set(g, []);
      m.get(g)!.push(s);
    }
    return sortGroups(Array.from(m.entries()));
  }, [stores]);

  const groupOptions = useMemo(
    () => sortGroupNames(Array.from(new Set(stores.map((s) => s.group || UNGROUPED)))),
    [stores],
  );

  // Full filtered set (group + free-text), globally sorted so a page slice keeps
  // each parent-company's rows contiguous.
  const flatRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const rows = (stockQ.data ?? []).filter((r) => {
      const g = r.storeGroup || UNGROUPED;
      if (group && g !== group) return false;
      if (term && !`${r.storeName} ${r.itemCode} ${r.itemBarcode} ${r.itemName}`.toLowerCase().includes(term)) return false;
      return true;
    });
    rows.sort((a, b) => {
      const ga = a.storeGroup || UNGROUPED, gb = b.storeGroup || UNGROUPED;
      const gc = ga === UNGROUPED ? 1 : gb === UNGROUPED ? -1 : ga.localeCompare(gb);
      return gc || a.storeName.localeCompare(b.storeName) || a.itemCode.localeCompare(b.itemCode);
    });
    return rows;
  }, [stockQ.data, search, group]);

  const total = flatRows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageSafe = Math.min(page, totalPages);
  const loading = stockQ.isFetching && (stockQ.data ?? []).length === 0;

  // Current page, then bucket that slice by parent company (order preserved).
  const grouped = useMemo(() => {
    const slice = flatRows.slice((pageSafe - 1) * pageSize, pageSafe * pageSize);
    const m = new Map<string, SisStockRow[]>();
    for (const r of slice) {
      const g = r.storeGroup || UNGROUPED;
      if (!m.has(g)) m.set(g, []);
      m.get(g)!.push(r);
    }
    return Array.from(m.entries());
  }, [flatRows, pageSafe, pageSize]);

  // Full-set totals per group (so bands stay accurate even when a group spans pages).
  const groupTotals = useMemo(() => {
    const m = new Map<string, { stores: Set<number>; lines: number; value: number }>();
    for (const r of flatRows) {
      const g = r.storeGroup || UNGROUPED;
      let e = m.get(g);
      if (!e) { e = { stores: new Set(), lines: 0, value: 0 }; m.set(g, e); }
      e.stores.add(r.partyId);
      e.lines += 1;
      e.value += r.stockValue;
    }
    return m;
  }, [flatRows]);

  // KPI summary over the whole filtered set (not just the visible page).
  const kpis = useMemo(() => {
    const stores = new Set(flatRows.map((r) => r.partyId)).size;
    let units = 0, value = 0, lowOut = 0;
    for (const r of flatRows) {
      units += r.qty;
      value += r.stockValue;
      if (r.stockStatus !== 'In Stock') lowOut += 1;
    }
    return { stores, items: flatRows.length, units, value, lowOut };
  }, [flatRows]);

  useEffect(() => {
    const update = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const docTop = rect.top + window.scrollY; // document-relative, stable across scroll
      const titleH = titleBarRef.current?.getBoundingClientRect().height ?? 0;
      const footerH = footerRef.current?.getBoundingClientRect().height ?? 0;
      const avail = window.innerHeight - docTop - titleH - footerH - 8;
      setMaxHeight(`${Math.max(160, avail)}px`);
    };
    update();
    const r = requestAnimationFrame(update);
    window.addEventListener('resize', update);
    return () => { cancelAnimationFrame(r); window.removeEventListener('resize', update); };
  }, [total, loading]);

  return (
    <div>
      <SectionHeader
        title="SIS Stock — Shop-in-Shop Inventory"      />

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <KpiCard label="Stores" value={kpis.stores} accent="#6366f1" Icon={Store} loading={loading} />
        <KpiCard label="Items" value={kpis.items} accent="#f59e0b" Icon={Package} loading={loading} />
        <KpiCard label="Units On Hand" value={kpis.units} accent="#3b82f6" Icon={Boxes} loading={loading} />
        <KpiCard label="Stock Value" value={`₹${formatINR(kpis.value)}`} accent="#10b981" Icon={Wallet} loading={loading} />
        <KpiCard label="Low / Out of Stock" value={kpis.lowOut} accent="#ef4444" Icon={AlertTriangle} loading={loading} />
      </div>

      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto">
          <div className="relative w-full">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="!pl-5 sm:!pl-8"
              aria-label="Search SIS stock"
            />
          </div>
        </FilterField>
        <FilterField label="Group">
          <Select value={group} onChange={(e) => setGroup(e.target.value)}>
            <option value="">All Groups</option>
            {groupOptions.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </Select>
        </FilterField>
        <FilterField label="Store">
          <Select value={partyId} onChange={(e) => setPartyId(e.target.value)}>
            <option value="">All Stores</option>
            {storesByGroup.map(([g, list]) => (
              <optgroup key={g} label={g}>
                {list.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s.city ? ` — ${s.city}` : ''}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
        </FilterField>
        <div className="col-span-2 flex flex-wrap items-end gap-2 sm:col-span-1 sm:ml-auto sm:self-end">
          <Button
            variant="glass"
            size="sm"
            disabled={exporting || total === 0}
            onClick={async () => {
              setExporting(true);
              try {
                // Long timeout — the export is uncapped (whole matching dataset).
                await downloadExcel(
                  sisStockExportPath({ partyId: partyId ? Number(partyId) : undefined, group: group || undefined, search }),
                  'GRHAMOY_SIS_Stock.xlsx',
                  120_000,
                );
              } finally {
                setExporting(false);
              }
            }}
            title="Export all SIS stock rows (current filters) to Excel"
          >
            <span className="inline-flex items-center gap-1"><FileSpreadsheet size={12} /> {exporting ? 'Exporting…' : 'Export'}</span>
          </Button>
          <Button variant="glass" size="sm" onClick={() => setLabelsOpen(true)} title="Configure each store's report label for group-wise uploads">
            <span className="inline-flex items-center gap-1"><Tags size={12} /> Labels</span>
          </Button>
          <Button
            variant="glass"
            size="sm"
            onClick={() =>
              downloadExcel(
                sisSalesReportTemplatePath('closing', uploadTarget === 'group'),
                `GRHAMOY_SIS_ClosingStock${uploadTarget === 'group' ? '_GroupWise' : ''}_Template.xlsx`,
              )
            }
            title={uploadTarget === 'group' ? 'Group-wise closing-stock template (with Store column)' : 'Closing-stock template'}
          >
            <span className="inline-flex items-center gap-1"><Download size={12} /> Template</span>
          </Button>
          <Button
            variant="glass"
            size="sm"
            disabled={!uploadTarget}
            onClick={() => setImportOpen(true)}
            title={
              uploadTarget === 'store' ? 'Upload this store’s closing-stock count'
              : uploadTarget === 'group' ? `Upload a group-wise closing-stock file for ${group}`
              : 'Select a store, or a group for a combined file'
            }
          >
            <span className="inline-flex items-center gap-1"><Upload size={12} /> {uploadTarget === 'group' ? 'Closing Stock (Group)' : 'Upload Closing Stock'}</span>
          </Button>
        </div>
      </FilterBar>

      <div ref={containerRef} className="overflow-hidden rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] backdrop-blur-xl">
        <div ref={titleBarRef} className="flex items-center justify-between gap-2 border-b border-[var(--border-color)] px-3 py-1.5 sm:px-4 sm:py-2">
          <div className="text-sm font-extrabold text-[var(--text-primary)]">SIS Stock</div>
          <div className="text-xs text-[var(--text-muted)]">
            {total} line{total !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Desktop / tablet — full grouped table (mobile uses cards below). */}
        <div
          className="hidden overflow-auto sm:block [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[var(--border-color)] [&::-webkit-scrollbar-track]:bg-transparent"
          style={{ height: maxHeight, scrollbarWidth: 'thin', scrollbarColor: 'var(--border-color) transparent' }}
        >
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10">
              <tr>
                {COLS.map((h, i) => (
                  <th
                    key={h || i}
                    className={`border-b-2 border-r border-[var(--border-color)] bg-orange-50 font-black uppercase tracking-wide text-accent whitespace-nowrap last:border-r-0 ${TH_DENSITY}`}
                    style={{ textAlign: i >= 6 && i <= 8 ? 'right' : i === 0 ? 'center' : 'left' }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={COLS.length} className="px-4 py-10 text-center text-sm" style={{ color: 'var(--text-muted)' }}>Loading…</td></tr>
              ) : total === 0 ? (
                <tr><td colSpan={COLS.length} className="px-4 py-10 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                  No SIS stock yet — dispatch to an MBO &amp; SiS customer to populate this.
                </td></tr>
              ) : (
                grouped.map(([g, arr]) => {
                  const t = groupTotals.get(g);
                  return (
                    <GroupBlock
                      key={g}
                      group={g}
                      storeCount={t?.stores.size ?? 0}
                      lines={t?.lines ?? arr.length}
                      value={t?.value ?? 0}
                      rows={arr}
                      onReorder={setReorderRow}
                      expanded={expanded}
                      onToggle={toggleExpanded}
                    />
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile — one card per stock line, grouped by parent company. */}
        <div className="overflow-y-auto sm:hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" style={{ height: maxHeight, WebkitOverflowScrolling: 'touch' }}>
          {loading ? (
            <div className="px-4 py-10 text-center text-sm text-[var(--text-muted)]">Loading…</div>
          ) : total === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-[var(--text-muted)]">
              No SIS stock yet — dispatch to an MBO &amp; SiS customer to populate this.
            </div>
          ) : (
            grouped.map(([g, arr]) => {
              const t = groupTotals.get(g);
              const storeCount = t?.stores.size ?? 0;
              return (
                <Fragment key={g}>
                  <div className="flex items-center gap-2 border-y border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-2">
                    <span className="inline-block h-2 w-2 rounded-full" style={{ background: 'var(--accent)' }} />
                    <span className="text-xs font-extrabold uppercase tracking-wide text-[var(--text-primary)]">{g}</span>
                    <span className="ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: 'rgba(255,140,0,0.12)', color: 'var(--accent)' }}>
                      {storeCount} store{storeCount !== 1 ? 's' : ''} · {t?.lines ?? arr.length} line{(t?.lines ?? arr.length) !== 1 ? 's' : ''} · {formatINR(t?.value ?? 0)}
                    </span>
                  </div>
                  {arr.map((r) => (
                    <MobileStockCard
                      key={`${r.partyId}-${r.itemId}`}
                      row={r}
                      open={expanded.has(`${r.partyId}-${r.itemId}`)}
                      onToggle={() => toggleExpanded(`${r.partyId}-${r.itemId}`)}
                      onReorder={() => setReorderRow(r)}
                    />
                  ))}
                </Fragment>
              );
            })
          )}
        </div>

        {total > 0 && (
          <div ref={footerRef} className="border-t border-[var(--border-color)] px-2 py-2 sm:px-3">
            <Pagination page={pageSafe} pageSize={pageSize} total={total} setPage={setPage} setPageSize={setPageSize} />
          </div>
        )}
      </div>

      <SetSisReorderModal
        open={reorderRow !== null}
        row={reorderRow}
        onClose={() => setReorderRow(null)}
      />

      {importOpen && uploadTarget && (
        <ExcelImportModal
          open={importOpen}
          onClose={() => setImportOpen(false)}
          title={`Closing Stock — ${uploadTarget === 'group' ? group : selectedStore!.name}`}
          expectedColumns={[
            ...(uploadTarget === 'group' ? ['Store'] : []),
            'Item Code', 'Closing Stock', 'Invoice No (optional)', 'Invoice Date (optional)', 'Sale Date',
          ]}
          onImport={(file, progress) => {
            const onProgress = (done: number, total: number) => progress(`Saving… ${done} of ${total} rows`);
            return uploadTarget === 'group'
              ? importSisGroupReport(group, file, 'closing', false, onProgress)
              : importSisSalesReport(selectedStore!.id, file, 'closing', false, onProgress);
          }}
          onSuccess={() => {
            qc.invalidateQueries({ queryKey: ['sis-stock'] });
            qc.invalidateQueries({ queryKey: ['sis-ledger'] });
            qc.invalidateQueries({ queryKey: ['sis-exceptions'] });
            qc.invalidateQueries({ queryKey: ['sis-exceptions-summary'] });
          }}
        />
      )}

      <StoreLabelsModal open={labelsOpen} onClose={() => setLabelsOpen(false)} group={group || undefined} />
    </div>
  );
}

function GroupBlock({
  group,
  storeCount,
  lines,
  value,
  rows,
  onReorder,
  expanded,
  onToggle,
}: {
  group: string;
  storeCount: number;
  lines: number;
  value: number;
  rows: SisStockRow[];
  onReorder: (r: SisStockRow) => void;
  expanded: Set<string>;
  onToggle: (key: string) => void;
}) {
  return (
    <>
      {/* Group header band spanning the whole table */}
      <tr className="bg-[var(--bg-secondary)]">
        <td colSpan={COLS.length} className="h-9 border-y border-[var(--border-color)] px-3 py-0">
          <span className="inline-flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full" style={{ background: 'var(--accent)' }} />
            <span className="text-xs font-extrabold uppercase tracking-wide" style={{ color: 'var(--text-primary)' }}>{group}</span>
            <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: 'rgba(255,140,0,0.12)', color: 'var(--accent)' }}>
              {storeCount} store{storeCount !== 1 ? 's' : ''} · {lines} line{lines !== 1 ? 's' : ''} · {formatINR(value)}
            </span>
          </span>
        </td>
      </tr>
      {rows.map((r, idx) => {
        const key = `${r.partyId}-${r.itemId}`;
        const isOpen = expanded.has(key);
        const cell = `border-r border-[var(--border-color)] font-semibold whitespace-nowrap last:border-r-0 ${TD_DENSITY}`;
        return (
          <Fragment key={key}>
            <tr
              className={`border-b border-[var(--border-color)] transition-colors hover:bg-[var(--bg-secondary)] ${idx % 2 === 1 ? 'bg-[var(--bg-secondary)]/60' : ''}`}
            >
              <td className={`${cell} text-center`}>
                <button
                  type="button"
                  onClick={() => onToggle(key)}
                  className="rounded p-0.5 text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-accent"
                  title={isOpen ? 'Hide invoice breakdown' : 'Show invoice breakdown'}
                  aria-expanded={isOpen}
                >
                  <ChevronRight
                    size={13}
                    className="transition-transform"
                    style={{ transform: isOpen ? 'rotate(90deg)' : 'none' }}
                  />
                </button>
              </td>
              <td className={`${cell} whitespace-nowrap`} style={{ color: 'var(--text-secondary)' }}>
                <span className="font-bold">{r.storeName}{r.storeCity ? ` (${r.storeCity})` : ''}</span>
              </td>
              <td className={`${cell} font-extrabold text-accent whitespace-nowrap`}>{r.itemCode}</td>
              <td className={`${cell} whitespace-nowrap`} style={{ color: 'var(--text-secondary)' }}>{r.itemBarcode || '—'}</td>
              <td className={cell} style={{ color: 'var(--text-primary)' }}>
                <div className="flex items-center gap-2">
                  <ItemThumb src={r.imageUrl} size={28} />
                  <span>{r.itemName}</span>
                </div>
              </td>
              <td className={cell} style={{ color: 'var(--text-secondary)' }}>{r.unit}</td>
              <td className={`${cell} text-right font-bold`} style={{ color: 'var(--text-primary)' }}>{r.qty}</td>
              <td className={`${cell} text-right`}>
                <button
                  type="button"
                  onClick={() => onReorder(r)}
                  className="rounded px-1.5 py-0.5 text-xs font-semibold text-accent hover:bg-[var(--bg-soft)]"
                  title="Set reorder level"
                >
                  {r.reorderLevel || '—'}
                </button>
              </td>
              <td className={`${cell} text-right`} style={{ color: 'var(--text-secondary)' }}>{formatINR(r.stockValue)}</td>
              <td className={cell}><Badge tone={STATUS_TONE[r.stockStatus]}>{r.stockStatus}</Badge></td>
              <td className={cell}>
                <Link
                  to={`/inventory/sis-ledger?partyId=${r.partyId}&itemId=${r.itemId}`}
                  className="rounded border border-[var(--border-soft)] px-2 py-1 text-xs hover:bg-[var(--bg-soft)]"
                >
                  Ledger
                </Link>
              </td>
            </tr>
            {isOpen && (
              <tr className="border-b border-[var(--border-color)]">
                <td colSpan={COLS.length} className="p-0" style={{ background: 'var(--bg-secondary)' }}>
                  <StockLotsPanel partyId={r.partyId} itemId={r.itemId} onHand={r.qty} />
                </td>
              </tr>
            )}
          </Fragment>
        );
      })}
    </>
  );
}

/** Mobile card for one stock line — mirrors the shared Table's card treatment. */
function MobileStockCard({
  row,
  open,
  onToggle,
  onReorder,
}: {
  row: SisStockRow;
  open: boolean;
  onToggle: () => void;
  onReorder: () => void;
}) {
  return (
    <div className="border-b border-l-4 border-l-accent border-[var(--border-color)] px-3 py-2.5 last:border-b-0 odd:bg-[var(--bg-secondary)]/40">
      <div className="mb-1.5 flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <ItemThumb src={row.imageUrl} />
          <div className="min-w-0">
            <div className="truncate text-[13px] font-extrabold text-[var(--text-primary)]">{row.itemName}</div>
            <div className="truncate text-[11px] font-bold text-accent">{row.itemCode}</div>
          </div>
        </div>
        <Badge tone={STATUS_TONE[row.stockStatus]}>{row.stockStatus}</Badge>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px]">
        <Cell label="Store" value={`${row.storeName}${row.storeCity ? ` (${row.storeCity})` : ''}`} />
        {row.itemBarcode && <Cell label="Barcode" value={row.itemBarcode} />}
        <Cell label="Qty" value={<span className="font-bold text-[var(--text-primary)]">{row.qty} {row.unit}</span>} />
        <Cell label="Value" value={`₹${formatINR(row.stockValue)}`} />
        <Cell label="Reorder" value={String(row.reorderLevel || '—')} />
      </dl>
      <div className="mt-2 flex items-center gap-2 border-t border-[var(--border-color)]/50 pt-2">
        <button
          type="button"
          onClick={onToggle}
          className="inline-flex flex-1 items-center justify-center gap-1 rounded border border-[var(--border-soft)] px-2 py-1 text-[11px] font-semibold hover:bg-[var(--bg-soft)]"
          aria-expanded={open}
        >
          <ChevronRight size={12} className="transition-transform" style={{ transform: open ? 'rotate(90deg)' : 'none' }} />
          Invoice lots
        </button>
        <button
          type="button"
          onClick={onReorder}
          className="flex-1 rounded border border-[var(--border-soft)] px-2 py-1 text-[11px] font-semibold text-accent hover:bg-[var(--bg-soft)]"
        >
          Set reorder
        </button>
        <Link
          to={`/inventory/sis-ledger?partyId=${row.partyId}&itemId=${row.itemId}`}
          className="flex-1 rounded border border-[var(--border-soft)] px-2 py-1 text-center text-[11px] font-semibold hover:bg-[var(--bg-soft)]"
        >
          Ledger
        </Link>
      </div>
      {open && (
        <div className="mt-2 overflow-x-auto rounded-card border border-[var(--border-color)]" style={{ background: 'var(--bg-secondary)' }}>
          <StockLotsPanel partyId={row.partyId} itemId={row.itemId} onHand={row.qty} compact />
        </div>
      )}
    </div>
  );
}

function Cell({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="col-span-2 grid grid-cols-subgrid items-baseline">
      <dt className="truncate text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">{label}</dt>
      <dd className="min-w-0 break-words font-semibold text-[var(--text-primary)]">{value}</dd>
    </div>
  );
}

/** Lazily-loaded invoice-wise breakdown of one item's on-hand stock at a store. */
function StockLotsPanel({ partyId, itemId, onHand, compact }: { partyId: number; itemId: number; onHand: number; compact?: boolean }) {
  const { data, isFetching } = useQuery({
    queryKey: ['sis-stock-lots', partyId, itemId],
    queryFn: () => listSisStockLots(partyId, itemId),
    staleTime: 0,
  });
  const lots = data ?? [];

  return (
    <div className={compact ? 'px-3 py-3' : 'px-10 py-3'}>
      <div className="mb-2 text-[10px] font-extrabold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
        On-hand by invoice · oldest first (FIFO)
      </div>
      {isFetching && lots.length === 0 ? (
        <div className="py-3 text-xs" style={{ color: 'var(--text-muted)' }}>Loading invoice lots…</div>
      ) : lots.length === 0 ? (
        <div className="py-3 text-xs" style={{ color: 'var(--text-muted)' }}>
          No priced invoice lots on record for this item{onHand > 0 ? ' (stock predates lot tracking).' : '.'}
        </div>
      ) : (
        <table className="w-full max-w-3xl text-xs">
          <thead>
            <tr style={{ color: 'var(--text-muted)' }}>
              <th className="px-3 py-1.5 text-left text-[10px] font-extrabold uppercase tracking-wider">Invoice No</th>
              <th className="px-3 py-1.5 text-left text-[10px] font-extrabold uppercase tracking-wider">Date</th>
              <th className="px-3 py-1.5 text-right text-[10px] font-extrabold uppercase tracking-wider">Rate</th>
              <th className="px-3 py-1.5 text-right text-[10px] font-extrabold uppercase tracking-wider">Issued</th>
              <th className="px-3 py-1.5 text-right text-[10px] font-extrabold uppercase tracking-wider">On Hand</th>
              <th className="px-3 py-1.5 text-right text-[10px] font-extrabold uppercase tracking-wider">Value</th>
            </tr>
          </thead>
          <tbody>
            {lots.map((l) => (
              <tr key={l.id} style={{ borderTop: '1px solid var(--border-color)' }}>
                <td className="px-3 py-1.5 font-bold text-accent whitespace-nowrap">{l.invoiceNo}</td>
                <td className="px-3 py-1.5 whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{formatDate(l.lotDate)}</td>
                <td className="px-3 py-1.5 text-right" style={{ color: 'var(--text-secondary)' }}>{formatINR(l.rate)}</td>
                <td className="px-3 py-1.5 text-right" style={{ color: 'var(--text-secondary)' }}>{l.qtyIn}</td>
                <td className="px-3 py-1.5 text-right font-bold" style={{ color: 'var(--text-primary)' }}>{l.qtyRemaining}</td>
                <td className="px-3 py-1.5 text-right" style={{ color: 'var(--text-secondary)' }}>{formatINR(l.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** Alphabetical, with the "Ungrouped" bucket always last. */
function sortGroups<T>(entries: [string, T][]): [string, T][] {
  return entries.sort(([a], [b]) => (a === UNGROUPED ? 1 : b === UNGROUPED ? -1 : a.localeCompare(b)));
}
function sortGroupNames(names: string[]): string[] {
  return names.sort((a, b) => (a === UNGROUPED ? 1 : b === UNGROUPED ? -1 : a.localeCompare(b)));
}
